import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  exportFactoringInvoices,
  getExtractionStatus,
  getFactoringCreditLineDetail,
  getFactoringCreditLineList,
  getFactoringInvoices,
  getFactoringProductRequests,
  getFactoringSoldSummary,
  refreshInvoices,
  sellInvoices,
  submitFactoringProductRequest,
  syncFactoringCollateral,
} from '../services/factoringCreditLine';
import {
  REVOLVING_CREDIT_LINE_DETAIL_QUERY_KEY,
  REVOLVING_CREDIT_LINE_LIST_QUERY_KEY,
  useSubmitDrawRequest,
} from './useRevolvingCreditLine';
import { PaginatedResponse } from '../services/types';
import { Nullable } from '../types';
import {
  FactoringCreditLineClient,
  FactoringCreditLineDetail,
  FactoringExtractionStatus,
  FactoringInvoice,
  FactoringInvoicesFilters,
  FactoringProductRequest,
  FactoringCollateralSync,
  FactoringSoldSummary,
  SellFactoringInvoicesPayload,
  SellFactoringInvoicesResponse,
} from '../types/factoringCreditLine';

export const FACTORING_INVOICES_QUERY_KEY = 'factoring-invoices';
export const FACTORING_EXTRACTION_STATUS_QUERY_KEY = 'factoring-extraction-status';
export const FACTORING_SOLD_SUMMARY_QUERY_KEY = 'factoring-sold-summary';
export const FACTORING_PRODUCT_REQUESTS_QUERY_KEY = 'factoring-product-requests';
export const FACTORING_COLLATERAL_QUERY_KEY = 'factoring-collateral';

export const EXTRACTION_POLL_INTERVAL_MS = 2000;
export const SEARCH_DEBOUNCE_MS = 400;

// Debounces a value used inside a query key (React 17 has no useDeferredValue).
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delayMs);
    return () => clearTimeout(timeout);
  }, [value, delayMs]);

  return debouncedValue;
}

// The factoring credit line is a regular RCL credit line (product_type=factoring), so list and
// detail share the RCL query keys: draw/payment mutations invalidate both products at once.
export const useFactoringCreditLineList = (companyId: number | undefined) => {
  return useQuery<PaginatedResponse<FactoringCreditLineClient>>({
    queryKey: [REVOLVING_CREDIT_LINE_LIST_QUERY_KEY, companyId],
    queryFn: () => getFactoringCreditLineList(companyId!),
    enabled: !!companyId,
  });
};

export const useFactoringCreditLineDetail = (
  companyId: number | undefined,
  creditLineId: string | undefined
) => {
  return useQuery<FactoringCreditLineDetail>({
    queryKey: [REVOLVING_CREDIT_LINE_DETAIL_QUERY_KEY, companyId, creditLineId],
    queryFn: () => getFactoringCreditLineDetail(companyId!, creditLineId!),
    enabled: !!companyId && !!creditLineId,
  });
};

export const useFactoringSoldSummary = (companyId: number | undefined) => {
  return useQuery<FactoringSoldSummary>({
    queryKey: [FACTORING_SOLD_SUMMARY_QUERY_KEY, companyId],
    queryFn: () => getFactoringSoldSummary(companyId!),
    enabled: !!companyId,
  });
};

export const useFactoringInvoices = (companyId: number | undefined, filters: FactoringInvoicesFilters) => {
  return useQuery<PaginatedResponse<FactoringInvoice>>({
    queryKey: [
      FACTORING_INVOICES_QUERY_KEY,
      companyId,
      filters.page,
      filters.pageSize,
      filters.search,
      filters.eligibility,
      filters.scope,
      filters.state,
      filters.status,
    ],
    queryFn: () => getFactoringInvoices(companyId!, filters),
    enabled: !!companyId,
    keepPreviousData: true,
  });
};

export const useExtractionPolling = (companyId: number | undefined) => {
  const queryClient = useQueryClient();
  const previousInvoiceCountRef = useRef<Nullable<number>>(null);

  return useQuery<FactoringExtractionStatus>({
    queryKey: [FACTORING_EXTRACTION_STATUS_QUERY_KEY, companyId],
    queryFn: () => getExtractionStatus(companyId!),
    enabled: !!companyId,
    // Poll every ~2s while the extraction is running and the screen is mounted; stop when it
    // completes (conditional refetchInterval pattern, see useBankingTransferFees in useTransfers.ts).
    refetchInterval: (data) => (data && !data.complete ? EXTRACTION_POLL_INTERVAL_MS : false),
    onSuccess: (data) => {
      // New invoices were classified since the last poll: refresh the invoices list.
      if (previousInvoiceCountRef.current !== null && previousInvoiceCountRef.current !== data.invoiceCount) {
        queryClient.invalidateQueries({ queryKey: [FACTORING_INVOICES_QUERY_KEY, companyId] });
      }
      previousInvoiceCountRef.current = data.invoiceCount;
    },
  });
};

// Recomputes the company's collateral on entering the factoring screens; the RCL credit line
// queries are invalidated so Collateral / Availability refresh.
export const useSyncFactoringCollateral = (companyId: number | undefined, enabled: boolean) => {
  const queryClient = useQueryClient();

  return useQuery<FactoringCollateralSync>({
    queryKey: [FACTORING_COLLATERAL_QUERY_KEY, companyId],
    queryFn: () => syncFactoringCollateral(companyId!),
    enabled: !!companyId && enabled,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REVOLVING_CREDIT_LINE_LIST_QUERY_KEY, companyId] });
      queryClient.invalidateQueries({ queryKey: [REVOLVING_CREDIT_LINE_DETAIL_QUERY_KEY, companyId] });
    },
  });
};

export const useSellInvoices = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<SellFactoringInvoicesResponse, unknown, SellFactoringInvoicesPayload>({
    mutationFn: (payload) => sellInvoices(companyId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FACTORING_INVOICES_QUERY_KEY, companyId] });
      queryClient.invalidateQueries({ queryKey: [FACTORING_EXTRACTION_STATUS_QUERY_KEY, companyId] });
      queryClient.invalidateQueries({ queryKey: [FACTORING_SOLD_SUMMARY_QUERY_KEY, companyId] });
      queryClient.invalidateQueries({ queryKey: [REVOLVING_CREDIT_LINE_LIST_QUERY_KEY, companyId] });
      queryClient.invalidateQueries({ queryKey: [REVOLVING_CREDIT_LINE_DETAIL_QUERY_KEY, companyId] });
    },
  });
};

export const useRefreshInvoices = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (automatic?: boolean) => refreshInvoices(companyId!, automatic),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FACTORING_EXTRACTION_STATUS_QUERY_KEY, companyId] });
      queryClient.invalidateQueries({ queryKey: [FACTORING_INVOICES_QUERY_KEY, companyId] });
    },
  });
};

export const useExportFactoringInvoices = () => {
  return useMutation({
    mutationFn: ({
      companyId,
      filters,
    }: {
      companyId: number;
      filters?: Partial<
        Pick<FactoringInvoicesFilters, 'search' | 'eligibility' | 'scope' | 'state' | 'status'>
      >;
    }) => exportFactoringInvoices(companyId, filters),
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factoring_invoices_${variables.companyId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

// Product enablement request: list the company's requests and submit a new one.
export const useFactoringProductRequests = (companyId: number | undefined) => {
  return useQuery<FactoringProductRequest[]>({
    queryKey: [FACTORING_PRODUCT_REQUESTS_QUERY_KEY, companyId],
    queryFn: () => getFactoringProductRequests(companyId!),
    enabled: !!companyId,
  });
};

export const useSubmitFactoringProductRequest = (companyId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestedAmount: number) => submitFactoringProductRequest(companyId!, requestedAmount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FACTORING_PRODUCT_REQUESTS_QUERY_KEY, companyId] });
    },
  });
};

// Draws against the factoring credit line go through the existing RCL draw flow.
export const useSubmitFactoringDraw = useSubmitDrawRequest;
